/**
 * Split an assistant answer into plain text and the links inside it.
 *
 * The answer is rendered as a React text node, so a URL in it arrives as
 * characters and nothing else. That is the whole reason this exists: the
 * knowledge corpus can point at a VELUX tutorial for a step where watching
 * beats reading, and the system prompt tells the model to point at
 * support@velux.com. Neither is reachable when a stuck homeowner has to copy it
 * off a phone screen by hand.
 *
 * Only hosts VELUX publishes or the kit depends on become links. The answer
 * comes from a model, and a model can produce a plausible-looking domain that
 * does not exist or is not ours. A wrong link in front of someone already stuck
 * is worse than no link, so anything unrecognised stays as text.
 *
 * In practice only the URLs reach this now. An answer carrying an email address
 * is withheld before it is rendered (`answerForTranscript` in `escalation.ts`),
 * because a resident is never told to write to anyone: we record the problem
 * and VELUX reaches out. The mailto branch stays because the rule belongs to
 * the copy, not to the mechanism, and text is not the place to enforce it.
 */

/** One run of an answer: either plain text, or something to make tappable. */
export type Segment = { kind: "text"; text: string } | { kind: "link"; text: string; href: string };

/**
 * Hosts allowed to become links, matched on the host itself or any subdomain.
 * Keep this short. Every addition is a domain we are willing to send a
 * homeowner to on the model's word.
 */
const LINKABLE_HOSTS = [
  "velux.com",
  "youtube.com",
  "youtu.be",
  "nabucasa.com",
  "home-assistant.io",
  "airthings.com",
  "boardofinnovation.ai",
];

/**
 * Punctuation a sentence leaves stuck to a URL, which is not part of it.
 * A closing parenthesis is handled separately, in `trimSentenceEnd`.
 */
const TRAILING_PUNCTUATION = /[.,;:!?\]}'"]+$/;

/**
 * Anything that might be an address. Case-insensitive because a scheme is, and
 * `new URL` lowercases it for us on the way out.
 *
 * The third alternative catches a domain written without a scheme, which is how
 * prose usually reads: "see www.velux.com/support". It matches loosely on
 * purpose, including things that are not addresses at all, because
 * `LINKABLE_HOSTS` is what decides. A filename like `linkify.ts` matches here
 * and is then thrown away, which is cheaper than a regex that tries to be
 * clever about it. Alternatives are ordered so a full URL and an email address
 * both win against it.
 */
const CANDIDATE = new RegExp(
  [
    "https?://[^\\s<>]+", // a URL with its scheme
    "[^\\s<>@]+@[^\\s<>@]+\\.[^\\s<>@]+", // an email address
    "(?:[a-z0-9-]+\\.)+[a-z]{2,}(?:/[^\\s<>]*)?", // a domain, maybe with a path
  ].join("|"),
  "gi",
);

/**
 * Break `text` into renderable segments.
 *
 * Args:
 *   text: The answer as the model wrote it.
 *
 * Returns:
 *   Segments in order. Text that contained no linkable address comes back as a
 *   single text segment, so the common case costs one allocation.
 */
export function splitLinks(text: string): Segment[] {
  const segments: Segment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(CANDIDATE)) {
    const start = match.index;
    const candidate = trimSentenceEnd(match[0]);
    const href = toHref(candidate);

    // Not somewhere we will send anyone. Leaving the cursor alone keeps the
    // address inside the surrounding text, exactly as the model wrote it.
    if (!href) {
      continue;
    }

    if (start > cursor) {
      segments.push({ kind: "text", text: text.slice(cursor, start) });
    }
    segments.push({ kind: "link", text: candidate, href });
    cursor = start + candidate.length;
  }

  if (cursor < text.length) {
    segments.push({ kind: "text", text: text.slice(cursor) });
  }
  return segments;
}

/**
 * Trim what a sentence left on a URL without eating what belongs to it.
 *
 * A trailing `)` is ambiguous. In "(watch https://example.com/a)" it closes a
 * parenthetical, but a path can legitimately end in one. Counting decides:
 * only an unmatched bracket comes off. The loop runs because either can be
 * outermost, as in "(see https://example.com/a.)".
 */
function trimSentenceEnd(raw: string): string {
  let value = raw;
  for (;;) {
    const stripped = value.replace(TRAILING_PUNCTUATION, "");
    if (stripped.endsWith(")") && occurrences(stripped, ")") > occurrences(stripped, "(")) {
      value = stripped.slice(0, -1);
      continue;
    }
    return stripped;
  }
}

function occurrences(text: string, character: string): number {
  let total = 0;
  for (const found of text) {
    if (found === character) {
      total += 1;
    }
  }
  return total;
}

/** The href for a candidate, or null when it is not one we will link. */
function toHref(candidate: string): string | null {
  if (candidate.includes("://")) {
    let url: URL;
    try {
      url = new URL(candidate);
    } catch {
      return null;
    }
    // HTTPS only. Every host below serves it, so an `http:` link from the model
    // is either a mistake or a downgrade, and neither is worth handing over.
    // `javascript:` and friends fail here too.
    if (url.protocol !== "https:") {
      return null;
    }
    return isLinkable(url.hostname) ? url.toString() : null;
  }

  // An `@` before any slash is an address. After one it is part of a path, as
  // in a channel URL like `youtube.com/@velux`.
  if (candidate.includes("@") && !candidate.includes("/")) {
    const domain = candidate.slice(candidate.lastIndexOf("@") + 1);
    return isLinkable(domain) ? `mailto:${candidate}` : null;
  }

  // A domain with no scheme. We pick https rather than trust one we were not
  // given, which is the same call as refusing `http:` above.
  let url: URL;
  try {
    url = new URL(`https://${candidate}`);
  } catch {
    return null;
  }
  return isLinkable(url.hostname) ? url.toString() : null;
}

function isLinkable(host: string): boolean {
  const lower = host.toLowerCase();
  return LINKABLE_HOSTS.some((allowed) => lower === allowed || lower.endsWith(`.${allowed}`));
}
